// UI for turning notifications on: a dismissible card (feed) and a switch row (Tôi).
// Permission is only ever requested from these explicit taps.

import { Capacitor } from '@capacitor/core';
import { h, toast } from './dom';
import { disablePush, enablePush, pushState, type PushState } from './push';

const DISMISS_KEY = 'booth.pushCardDismissed';

const label = (s: PushState) => ({
  on: 'Đã bật',
  off: 'Chưa bật',
  blocked: Capacitor.isNativePlatform() ? 'Bị chặn trong cài đặt máy' : 'Bị chặn trong trình duyệt',
  unsupported: 'Chưa hỗ trợ trên trình duyệt này',
}[s]);

const afterEnable = (s: PushState) => {
  if (s === 'on') toast('Đã bật thông báo ♡');
  else if (s === 'blocked') toast(Capacitor.isNativePlatform()
    ? 'Thông báo đang bị chặn. Mở Cài đặt → Ứng dụng → Booth Together để bật.'
    : 'Thông báo đang bị chặn. Bấm biểu tượng ổ khoá cạnh địa chỉ trang để cho phép.', 5000);
  else if (s === 'unsupported') toast('Trình duyệt này chưa hỗ trợ thông báo đẩy', 3500);
};

/** Feed card: only when notifications are off (not blocked/unsupported) and not dismissed. */
export function pushCard(): HTMLElement {
  const box = h('div.pushcard-slot');
  try { if (localStorage.getItem(DISMISS_KEY)) return box; } catch { /* */ }
  void pushState().then((s) => {
    if (s !== 'off') return;
    const btn = h('button.primary.small', {}, 'Bật thông báo');
    const card = h('aside.pushcard', {},
      h('span.pc-icon', { 'aria-hidden': 'true' }, '🔔'),
      h('div.pc-text', {}, h('b', {}, 'Biết ngay khi bạn bè bình luận'), h('small', {}, 'Lời mời chụp, bình luận và ảnh mới — không bỏ lỡ gì.')),
      btn,
      h('button.pc-x', {
        'aria-label': 'Ẩn', title: 'Ẩn',
        onclick: () => { try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* */ } card.remove(); },
      }, '✕'));
    btn.onclick = async () => {
      btn.disabled = true;
      const st = await enablePush().catch(() => 'unsupported' as const);
      afterEnable(st);
      if (st !== 'off') card.remove();
      btn.disabled = false;
    };
    box.replaceChildren(card);
  });
  return box;
}

/** Tôi: "Thông báo" row with a switch and the current state. */
export function pushRow(): HTMLElement {
  const state = h('small', {}, '…');
  const sw = h('input.switch', { type: 'checkbox', role: 'switch', disabled: true, 'aria-label': 'Thông báo' });
  const row = h('label.urow.pushrow', {},
    h('span.roomicon', { 'aria-hidden': 'true' }, '🔔'),
    h('div.umain', {}, h('b', {}, 'Thông báo'), state),
    sw);
  const paint = (s: PushState) => {
    state.textContent = label(s);
    sw.checked = s === 'on';
    sw.disabled = s === 'unsupported';
    row.classList.toggle('blocked', s === 'blocked');
  };
  void pushState().then(paint);
  sw.onchange = async () => {
    sw.disabled = true;
    if (sw.checked) {
      const s = await enablePush().catch(() => 'unsupported' as const);
      afterEnable(s);
      paint(s);
    } else {
      await disablePush().catch(() => {});
      toast('Đã tắt thông báo trên thiết bị này');
      paint(await pushState());
    }
  };
  return row;
}
